/**
 * パートナーマップのピンとポップアップ。
 *
 * 座標は「手動指定 → エリア名 → 都道府県(県庁所在地)」の順で決める。
 * 同じ座標に複数人いる場合はピンを1つにまとめ、ポップアップで全員を出す。
 */
(() => {
  const read = (id) => {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const points = read('pm-points');
  const partners = read('pm-partners');
  const pinLayer = document.getElementById('pm-pins');
  const dialog = document.getElementById('pm-dialog');
  const dialogBody = document.getElementById('pm-dialog-body');
  if (!points || !partners || !pinLayer || !dialog) return;

  /** その人をどこに置くか。決められなければ null(一覧にだけ出る) */
  const locate = (p) => {
    if (typeof p.pinX === 'number' && typeof p.pinY === 'number') {
      return { x: p.pinX, y: p.pinY, label: p.area || p.prefecture || '' };
    }
    if (p.area && points.areas[p.area]) {
      return { ...points.areas[p.area], label: `${p.prefecture} ${p.area}`.trim() };
    }
    if (p.prefecture && points.prefectures[p.prefecture]) {
      return { ...points.prefectures[p.prefecture], label: p.prefecture };
    }
    return null;
  };

  // 同じ座標の人をまとめる
  const spots = new Map();
  for (const p of partners) {
    const at = locate(p);
    if (!at) continue;

    const key = `${at.x.toFixed(2)},${at.y.toFixed(2)}`;
    if (!spots.has(key)) spots.set(key, { x: at.x, y: at.y, label: at.label, people: [] });
    spots.get(key).people.push(p);
  }

  const escape = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

  const openSpot = (spot) => {
    dialogBody.innerHTML = `
      <p class="pm__dialog-place">${escape(spot.label)}</p>
      <ul class="pm__dialog-list">
        ${spot.people
          .map(
            (p) => `
          <li style="--role-color:${escape(p.color)}">
            <a class="pm__dialog-item" ${p.link ? `href="${escape(p.link)}" target="_blank" rel="noopener"` : ''}>
              ${
                p.icon
                  ? `<img class="pm__icon" src="${escape(p.icon)}" alt="" width="52" height="52">`
                  : '<span class="pm__icon pm__icon--empty"></span>'
              }
              <span class="pm__dialog-text">
                <span class="pm__dialog-role">${escape(p.role)}</span>
                <span class="pm__dialog-name">${escape(p.name)}</span>
                ${p.style ? `<span class="pm__dialog-style">${escape(p.style)}</span>` : ''}
                ${p.link ? `<span class="pm__dialog-sns">@${escape(p.sns)}</span>` : ''}
              </span>
            </a>
          </li>`
          )
          .join('')}
      </ul>`;
    dialog.showModal();
  };

  const pinByKey = new Map();
  for (const [key, spot] of spots) {
    const pin = document.createElement('button');
    pin.type = 'button';
    pin.className = 'pm__pin';
    pin.style.left = `${spot.x}%`;
    pin.style.top = `${spot.y}%`;
    pin.style.setProperty('--pin-color', spot.people[0].color || '#111');
    pin.setAttribute('aria-label', `${spot.label} ${spot.people.map((p) => p.name).join('、')}`);
    if (spot.people.length > 1) {
      pin.innerHTML = `<span class="pm__pin-count">${spot.people.length}</span>`;
    }
    pin.addEventListener('click', () => openSpot(spot));
    pinLayer.appendChild(pin);

    pinByKey.set(key, pin);
    for (const p of spot.people) pin.dataset.keys = `${pin.dataset.keys || ''} ${p.key}`;
  }

  // 「地図」を押したピンを数秒だけ脈動させる。押したピンはそのまま開ける
  let located = null;
  let locatedTimer = 0;
  const flash = (pin) => {
    located?.classList.remove('is-located');
    clearTimeout(locatedTimer);
    pin.scrollIntoView({ block: 'center', behavior: 'smooth' });
    pin.classList.add('is-located');
    located = pin;
    locatedTimer = setTimeout(() => pin.classList.remove('is-located'), 4000);
  };

  // 一覧とピンを結びつける(スマホでは小さいピンを狙わずに済むように)
  for (const item of document.querySelectorAll('.pm__person')) {
    const key = item.dataset.partnerKey;
    const pin = [...pinByKey.values()].find((el) => (el.dataset.keys || '').split(' ').includes(key));
    if (!pin) continue;

    const toggle = (on) => {
      pin.classList.toggle('is-active', on);
      item.classList.toggle('is-active', on);
    };

    item.addEventListener('mouseenter', () => toggle(true));
    item.addEventListener('mouseleave', () => toggle(false));

    // 地図上の位置を見たい人のための導線。リンク本体はInstagramへ飛ばす
    const locator = document.createElement('button');
    locator.type = 'button';
    locator.className = 'pm__locate';
    locator.textContent = '地図';
    locator.title = '地図で位置を見る';
    locator.addEventListener('click', () => flash(pin));
    item.appendChild(locator);
  }

  // 逆向き。ピンに乗せたら、そこにいる人を一覧でも強調する
  for (const pin of pinByKey.values()) {
    const keys = (pin.dataset.keys || '').split(' ').filter(Boolean);
    const items = keys.flatMap((key) => [...document.querySelectorAll(`.pm__person[data-partner-key="${CSS.escape(key)}"]`)]);
    const toggle = (on) => {
      pin.classList.toggle('is-active', on);
      for (const item of items) item.classList.toggle('is-active', on);
    };
    pin.addEventListener('mouseenter', () => toggle(true));
    pin.addEventListener('mouseleave', () => toggle(false));
  }

  // 一覧を絶対配置しているぶん、親が高さを持てない。実測して押し広げる
  const stage = document.querySelector('.pm__stage');
  const side = document.getElementById('pm-side');
  if (stage && side) {
    const fit = () => {
      stage.style.minHeight = '';
      if (getComputedStyle(side).position !== 'absolute') return;
      const need = side.offsetHeight;
      if (need > stage.offsetHeight) stage.style.minHeight = `${need}px`;
    };
    fit();
    addEventListener('resize', fit);
    addEventListener('load', fit);
  }

  dialog.querySelector('.pm__dialog-close')?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
})();
